import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { imageUrl } from '../api/endpoints';

export type ProductConfirmationData = {
  barcode: string;
  product_id: number;
  brand?: string;
  name?: string;
  image_url?: string;
};

type ProductConfirmationDialogProps = {
  product: ProductConfirmationData;
  onConfirm: () => void;
  onCancel: () => void;
};

export default function ProductConfirmationDialog({
  product,
  onConfirm,
  onCancel,
}: ProductConfirmationDialogProps) {
  const displayName = product.name ?? 'Unknown Product';
  const displayBrand = product.brand ?? 'Unknown Brand';
  const imageSrc = product.image_url ? imageUrl(product.image_url) : null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.2 }}
        className="fixed inset-0 z-[70] flex items-end sm:items-center justify-center sm:p-6"
        aria-modal="true"
        role="dialog"
        onClick={(e) => { if (e.target === e.currentTarget) onCancel(); }}
      >
        {/* Scrim */}
        <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" />

        {/* Sheet */}
        <motion.div
          initial={{ y: 60, scale: 0.97, opacity: 0 }}
          animate={{ y: 0, scale: 1, opacity: 1 }}
          exit={{ y: 32, scale: 0.97, opacity: 0 }}
          transition={{ type: 'spring', stiffness: 420, damping: 34, mass: 0.82 }}
          className="relative w-full sm:max-w-sm rounded-t-[2rem] sm:rounded-[2rem] bg-white shadow-2xl ring-1 ring-gray-100/80 overflow-hidden"
          onClick={(e) => { e.stopPropagation(); }}
        >
          {/* Kivo accent stripe */}
          <div className="h-[3px] bg-gradient-to-r from-primary/70 via-primary to-violet-400/80" />

          {/* Drag pill (mobile only) */}
          <div className="sm:hidden flex justify-center pt-2.5">
            <div className="w-9 h-[3px] rounded-full bg-gray-200" />
          </div>

          {/* Hero image section */}
          {imageSrc ? (
            <div className="flex justify-center items-center pt-7 pb-5 px-6 bg-gradient-to-b from-gray-50/80 to-transparent">
              <motion.div
                initial={{ scale: 0.78, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ delay: 0.06, type: 'spring', stiffness: 360, damping: 24 }}
                className="relative w-32 h-32 rounded-[22px] bg-white shadow-xl ring-1 ring-black/6 flex items-center justify-center p-3 overflow-hidden"
              >
                <img src={imageSrc} alt={displayName} className="w-full h-full object-contain" />
              </motion.div>
            </div>
          ) : (
            <div className="flex justify-center pt-8 pb-4">
              <motion.div
                initial={{ scale: 0.8, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ delay: 0.06, type: 'spring', stiffness: 360, damping: 24 }}
                className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center"
              >
                <span className="material-symbols-outlined text-primary" style={{ fontSize: 30 }}>inventory_2</span>
              </motion.div>
            </div>
          )}

          {/* Text block */}
          <div className="px-7 text-center">
            <motion.p
              initial={{ y: 8, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.11 }}
              className="text-[11px] font-bold text-primary uppercase tracking-[0.15em] mb-2"
            >
              We found a match
            </motion.p>

            <motion.p
              initial={{ y: 6, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.16 }}
              className="text-[13px] font-medium text-gray-400"
            >
              {displayBrand}
            </motion.p>

            <motion.h4
              initial={{ y: 6, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.20 }}
              className="text-[1.25rem] font-bold tracking-tight text-gray-900 leading-snug mt-0.5"
            >
              {displayName}
            </motion.h4>

            <motion.div
              initial={{ opacity: 0, y: 4 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.25 }}
              className="inline-block mt-3 px-3 py-1 rounded-full bg-gray-100 text-[11px] text-gray-400 font-mono tracking-wide"
            >
              {product.barcode || 'No barcode'}
            </motion.div>

            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.28 }}
              className="mt-4 text-[13px] text-gray-500"
            >
              Is this the right product?
            </motion.p>
          </div>

          {/* Actions */}
          <div className="flex gap-3 px-5 pt-4 pb-7">
            <button
              type="button"
              onClick={onCancel}
              className="flex-1 rounded-2xl border border-gray-200 bg-white text-gray-600 px-4 py-3.5 text-sm font-semibold hover:bg-gray-50 hover:border-gray-300 active:scale-[0.97] transition-all duration-150"
            >
              No, try again
            </button>
            <button
              type="button"
              onClick={onConfirm}
              className="flex-1 rounded-2xl bg-primary text-white px-4 py-3.5 text-sm font-semibold hover:bg-primary/90 active:scale-[0.97] transition-all duration-150 shadow-md shadow-primary/25"
            >
              Yes, use this
            </button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
